/**
 * Clean NPC response text before displaying in chat.
 * Mirrors the legacy inline logic from lobbyStore to avoid any behavior changes.
 */
export function cleanNpcResponse(response: string): string {
  let cleaned = response;

  // `<tool_code>...</tool_code>` blocks and ```tool_code fenced blocks —
  // Gemini occasionally hallucinates tool calls as raw text instead of
  // routing through the structured function-calling channel. Stripped
  // at display time only — backend retains raw content because
  // Aldric/Kira's character-creation progress JSON (also appended
  // text-form) is load-bearing server-side state.
  cleaned = cleaned.replace(/<tool_code>[\s\S]*?<\/tool_code>/gi, '');
  cleaned = cleaned.replace(/```tool_code\b[\s\S]*?```/gi, '');

  // Remove === FILE === markers and everything after them (JSON data)
  // This handles both code-fenced and raw JSON formats
  const permanentIdx = cleaned.search(/===\s*PERMANENT\s*FILE\s*===/i);
  if (permanentIdx !== -1) {
    cleaned = cleaned.substring(0, permanentIdx);
  }

  // Also check for SESSION FILE in case PERMANENT wasn't found
  const sessionIdx = cleaned.search(/===\s*SESSION\s*FILE\s*===/i);
  if (sessionIdx !== -1) {
    cleaned = cleaned.substring(0, sessionIdx);
  }

  // === RECLAIM TEMPLATE === — Aldric's reclaim-path marker. Unlike the
  // forge markers above, narrative text often continues AFTER the JSON
  // (Aldric's "Welcome back, …" closer), so for COMPLETE blocks we strip
  // only the marker + immediately-following JSON block (flat, no nesting
  // in the reclaim schema) and preserve trailing prose.
  cleaned = cleaned.replace(
    /===\s*RECLAIM\s*TEMPLATE\s*===\s*\n?(?:```json\s*)?\{[^{}]*\}(?:\s*```)?\s*\n?/gi,
    ''
  );

  // Streaming fallback: if the marker is still present after the
  // complete-block pass, the JSON is mid-stream (no closing `}` yet) and
  // the partial `{"template_id":` would otherwise render. Truncate from
  // the marker to end-of-string — the closing narrative will land in a
  // later chunk and replay through the complete-block strip above.
  const reclaimIdx = cleaned.search(/===\s*RECLAIM\s*TEMPLATE\s*===/i);
  if (reclaimIdx !== -1) {
    cleaned = cleaned.substring(0, reclaimIdx);
  }

  // Partial-marker tail guard: stream chunk boundaries from Gemini routinely
  // split the marker mid-token, so the buffer can end with a fragment like
  // `=== RECLA` or `=== PERMANENT F` between WS frames. The complete-marker
  // strips above don't fire (they need the full `=== … ===` shape), so
  // without this the partial bytes paint into the chat bubble until the
  // next chunk arrives. Sweep every known marker, find the longest prefix
  // of the marker that matches the buffer's tail, and truncate from there.
  //
  // Threshold: `MIN_PARTIAL_LEN = 5` covers `=== R`/`=== P`/`=== S` (the
  // first capital after the leading `=== ` is enough to identify the
  // marker family) without false-positive-matching prose that happens to
  // end in `=== ` alone. Case-insensitive compare (uppercased copy) so we
  // catch markers regardless of LLM casing drift.
  //
  // Keep this list aligned with the complete-marker regexes above — adding
  // a new `=== XYZ ===` marker means adding it here too.
  const PARTIAL_MARKER_PREFIXES = [
    '=== PERMANENT FILE ===',
    '=== SESSION FILE ===',
    '=== RECLAIM TEMPLATE ===',
  ];
  const MIN_PARTIAL_LEN = 5;
  const cleanedUpper = cleaned.toUpperCase();
  for (const marker of PARTIAL_MARKER_PREFIXES) {
    for (let k = marker.length - 1; k >= MIN_PARTIAL_LEN; k--) {
      const prefix = marker.slice(0, k);
      if (cleanedUpper.endsWith(prefix)) {
        cleaned = cleaned.slice(0, cleaned.length - prefix.length);
        break;
      }
    }
  }

  // Remove remaining closed code fences (ANY language, not just `json`).
  // MUST run BEFORE the unclosed-fence strip below: the unclosed regex's
  // `[a-zA-Z]*` allows zero chars, so a CLOSING ``` reads as a new
  // opener and the regex would eat it + trailing prose, leaving the
  // original opener orphaned. Pre-fix (night-watch finding #11) this
  // only stripped `json` fences, so the orphan-then-eat path fired for
  // any other closed fence — ```text, ```md, bare ```` ``` ```` — the LLM
  // happened to emit. Widening to `[a-zA-Z]*` covers them all in one
  // sweep; the unclosed regex below then only ever sees a truly-unclosed
  // opener.
  cleaned = cleaned.replace(/```[a-zA-Z]*[\s\S]*?```/g, '');

  // Unclosed code fence at end-of-string — strips a partial ```json\n{…
  // (or ```anything\n…) block WHILE streaming (before the closing ```
  // arrives) so markdown doesn't render the open fence as a black code
  // box. Mirror of mobile's same-purpose strip at clean_npc_response.dart.
  cleaned = cleaned.replace(/\n*```[a-zA-Z]*\s*(?:(?!```)[\s\S])*$/g, '');

  // Remove standalone JSON objects with "status" field (completion status)
  // Only match simple single-line or small JSON without nested objects
  cleaned = cleaned.replace(/\{\s*"status"\s*:\s*"[^"]*"\s*\}/g, '');

  // Remove raw JSON fragments that leak through (alignment, equipment, etc.)
  // This catches JSON that starts mid-message without proper markers
  cleaned = cleaned.replace(
    /,?\s*"(alignment|equipment|features|traits|feats|spells|inventory|abilities|skills|proficiencies|saves|languages|backstory)"\s*:\s*[[{"][\s\S]*$/gi,
    ''
  );

  // Remove Aldric's progress tracking JSON (raw, without code fences)
  // The progress JSON is at the END of every response, so match from start pattern to end of string
  // This handles nested objects like {"decisions": {...}} correctly
  cleaned = cleaned.replace(
    /\s*\{\s*\n?\s*"(step_completed|current_step|ready_for_play)"[\s\S]*$/gi,
    ''
  );

  // Also catch progress JSON that starts with "decisions" (if step fields got cut off)
  cleaned = cleaned.replace(/\s*\{\s*\n?\s*"decisions"\s*:\s*\{[\s\S]*$/gi, '');

  // Remove any JSON arrays or objects that appear after narrative text
  // Look for patterns like: text... { "key": or text... [ {
  cleaned = cleaned.replace(/,?\s*[[{]\s*\n\s*"[a-z_]+"\s*:/gi, '');

  // Remove trailing horizontal rules (---, ___, ***) that LLMs sometimes add
  cleaned = cleaned.replace(/\n*[-_*]{3,}\s*$/g, '');

  // Clean up excessive whitespace
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
  return cleaned;
}
